import { apiClient } from '~/api/client';
import { fetchDocumentBlob } from '~/api/files';
import type { InvoiceApiPayload } from '~/schemas/invoice';
import type { components, operations } from '~/types/api';

/** O download por URL assinada mora em `~/api/files`, que os documentos da empresa também usam. */
export { fetchDocumentBlob };

export type InvoiceStatus = components['schemas']['InvoiceStatusEnum'];
export type InvoiceListItem = components['schemas']['InvoiceList'];
export type InvoiceListPage = components['schemas']['PaginatedInvoiceListList'];
export type InvoiceSummary = components['schemas']['InvoiceSummary'];
export type InvoiceItemDetail = components['schemas']['InvoiceItem'];
export type InvoiceDetail = components['schemas']['InvoiceDetail'];
/** O backend responde a criação com o detalhe completo da nota. */
export type CreatedInvoice = InvoiceDetail;
/** Códigos de motivo de cancelamento da NFSe Nacional (cMotivo). */
export type CancelReasonCode = components['schemas']['CancelReasonCodeEnum'];
export type FetchInvoicesParams = NonNullable<
  operations['api_invoices_list']['parameters']['query']
>;

export const INVOICE_TERMINAL_STATUSES: readonly InvoiceStatus[] = [
  'AUTHORIZED',
  'CANCELLED',
  'REJECTED',
];

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  DRAFT: 'Rascunho',
  PROCESSING: 'Processando',
  AUTHORIZED: 'Autorizada',
  REJECTED: 'Rejeitada',
  CANCELLED: 'Cancelada',
  ERROR: 'Erro',
};

export async function fetchInvoices(params: FetchInvoicesParams = {}): Promise<InvoiceListPage> {
  const response = await apiClient.get<InvoiceListPage | InvoiceListItem[]>('/api/invoices/', {
    params,
  });
  const data = response.data;
  if (Array.isArray(data)) {
    return { next: null, previous: null, results: data };
  }
  return data;
}

/**
 * Extrai o valor do parâmetro `cursor` de uma URL devolvida no campo `next`
 * do DRF CursorPagination. Devolve null se URL ausente ou cursor não existir.
 */
export function extractCursorFromUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.searchParams.get('cursor');
  } catch {
    return null;
  }
}

export async function fetchInvoiceSummary(client?: number): Promise<InvoiceSummary> {
  const response = await apiClient.get<InvoiceSummary>('/api/invoices/summary/', {
    params: client ? { client } : undefined,
  });
  return response.data;
}

/**
 * `idempotencyKey` precisa ser a mesma em toda tentativa da mesma emissão. Se o POST
 * chega ao servidor mas a resposta se perde, o reenvio devolve a nota já criada
 * (200) em vez de emitir uma segunda.
 */
export async function createInvoice(
  payload: InvoiceApiPayload,
  opts: { asDraft?: boolean; idempotencyKey?: string } = {},
): Promise<CreatedInvoice> {
  const params = opts.asDraft ? { draft: 1 } : undefined;
  const headers = opts.idempotencyKey ? { 'Idempotency-Key': opts.idempotencyKey } : undefined;
  const response = await apiClient.post<CreatedInvoice>('/api/invoices/', payload, {
    params,
    headers,
  });
  return response.data;
}

export async function sendInvoice(id: string): Promise<void> {
  await apiClient.post(`/api/invoices/${id}/send/`);
}

export async function fetchInvoice(id: string): Promise<InvoiceDetail> {
  const response = await apiClient.get<InvoiceDetail>(`/api/invoices/${id}/`);
  return response.data;
}

/** Motivos de cancelamento na ordem exibida no app. */
export const CANCEL_REASON_OPTIONS: ReadonlyArray<{ codigo: CancelReasonCode; label: string }> = [
  { codigo: '1', label: 'Erro na emissão' },
  { codigo: '2', label: 'Serviço não prestado' },
  { codigo: '9', label: 'Outros' },
];

export async function cancelInvoice(
  id: string,
  codigo: CancelReasonCode,
  reason: string,
): Promise<void> {
  await apiClient.post(`/api/invoices/${id}/cancel/`, { codigo, reason });
}

export async function deleteInvoice(id: string): Promise<void> {
  await apiClient.delete(`/api/invoices/${id}/`);
}

export async function retryInvoice(id: string): Promise<void> {
  await apiClient.post(`/api/invoices/${id}/retry/`);
}

export type DocumentKind = 'pdf' | 'xml';
export type DocumentUrl = components['schemas']['DocumentUrl'];

/**
 * URL assinada e temporária (1h) do PDF ou XML. Pedida no momento do uso: o link
 * não é público nem permanente, então ninguém acessa a nota sem estar autenticado.
 */
export async function fetchDocumentUrl(id: string, kind: DocumentKind): Promise<DocumentUrl> {
  const response = await apiClient.get<DocumentUrl>(`/api/invoices/${id}/document-url/`, {
    params: { kind },
  });
  return response.data;
}

/** Pede a URL assinada da nota e baixa o documento em seguida. */
export async function downloadInvoiceDocument(id: string, kind: DocumentKind): Promise<Blob> {
  const { url } = await fetchDocumentUrl(id, kind);
  return fetchDocumentBlob(url);
}
